import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { GraduationCap, BookOpen, Search, Activity, Clock, AlertCircle, MessageSquarePlus, Loader2 } from "lucide-react";
import { toast } from "sonner";

const ACTIVITY_LABELS = {
  view_course: "Consultation cours", start_quiz: "Quiz démarré", complete_quiz: "Quiz terminé",
  start_assignment: "Devoir démarré", complete_assignment: "Devoir rendu",
  chat_message: "Message IA", view_result: "Consultation notes", revision: "Révision",
};

export default function TeacherStudentsPage() {
  const [scope, setScope] = useState(null);
  const [students, setStudents] = useState([]);
  const [unrestricted, setUnrestricted] = useState(false);
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null); // { student, usage }
  const [commentDlg, setCommentDlg] = useState(null); // { student }
  const [comment, setComment] = useState("");
  const [savingComment, setSavingComment] = useState(false);

  const load = async () => {
    const [sc, st] = await Promise.allSettled([
      api.get("/sprint-f/teacher-scope/me"),
      api.get("/sprint-f/teacher-scope/students"),
    ]);
    if (sc.status === "fulfilled") setScope(sc.value.data);
    if (st.status === "fulfilled") {
      setStudents(st.value.data.students || []);
      setUnrestricted(!!st.value.data.unrestricted);
    } else toast.error("Impossible de charger les élèves");
  };
  useEffect(() => { load(); }, []);

  const openActivity = async (s) => {
    setDetail({ student: s, usage: null });
    try {
      const { data } = await api.get(`/sprint-f/ia-usage/student/${s.id}`, { params: { days: 30 } });
      setDetail({ student: s, usage: data });
    } catch { toast.error("Erreur chargement activité"); }
  };

  const openComment = async (s) => {
    setCommentDlg({ student: s });
    setComment("");
    try {
      const { data } = await api.get(`/weekly-report/comment/${s.id}`);
      setComment(data.comment || "");
    } catch { /* nouveau commentaire */ }
  };

  const saveComment = async () => {
    if (!comment.trim()) return toast.error("Commentaire vide");
    setSavingComment(true);
    try {
      await api.post("/weekly-report/comment", { student_id: commentDlg.student.id, comment });
      toast.success("Commentaire enregistré — il sera inclus dans le rapport hebdomadaire aux parents (samedi)");
      setCommentDlg(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSavingComment(false); }
  };

  const filtered = students.filter(s => {
    const t = `${s.first_name || ""} ${s.last_name || ""} ${s.matricule || ""}`.toLowerCase();
    return t.includes(q.toLowerCase());
  });

  // Group by class
  const byClass = {};
  filtered.forEach(s => {
    const c = s.class_name || "Sans classe";
    (byClass[c] = byClass[c] || []).push(s);
  });

  const noScope = scope && !scope.unrestricted && (scope.class_ids || []).length === 0;

  return (
    <div className="space-y-6" data-testid="teacher-students-page">
      <div>
        <div className="chip mb-2"><GraduationCap className="h-3 w-3" /><span>MES ÉLÈVES</span></div>
        <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Mes élèves</h1>
        <p className="text-slate-500 mt-1">Les élèves de vos classes affectées, avec leur activité pédagogique IA.</p>
      </div>

      {/* Scope chips */}
      {scope && !unrestricted && (
        <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm flex flex-wrap items-center gap-2" data-testid="my-scope">
          <span className="text-xs uppercase font-bold text-slate-500 mr-1">Mon périmètre :</span>
          {(scope.class_ids || []).map(c => (
            <span key={c} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-blue-50 text-[#1E3A8A] font-semibold"><GraduationCap className="h-3 w-3" />{c}</span>
          ))}
          {(scope.subjects || []).map(s => (
            <span key={s} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-green-50 text-[#166534] font-semibold"><BookOpen className="h-3 w-3" />{s}</span>
          ))}
          {noScope && (
            <span className="inline-flex items-center gap-2 text-sm text-orange-700 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
              <AlertCircle className="h-4 w-4" />Aucune classe affectée — contactez l&apos;administration pour définir votre périmètre.
            </span>
          )}
        </div>
      )}

      <div className="relative max-w-sm">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input placeholder="Rechercher un élève..." value={q} onChange={e => setQ(e.target.value)} className="pl-9" data-testid="ts-search" />
      </div>

      {Object.entries(byClass).map(([cls, rows]) => (
        <div key={cls} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 font-heading font-bold text-[#1E3A8A] flex items-center gap-2">
            <GraduationCap className="h-4 w-4" />{cls} <span className="text-xs font-normal text-slate-500">({rows.length} élève{rows.length > 1 ? "s" : ""})</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-2 text-left">Élève</th>
                  <th className="px-4 py-2 text-left">Matricule</th>
                  <th className="px-4 py-2 text-right">Activité IA</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(s => (
                  <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`ts-row-${s.id}`}>
                    <td className="px-4 py-2 font-medium">{s.first_name} {s.last_name}</td>
                    <td className="px-4 py-2 text-slate-500 text-xs">{s.matricule || "—"}</td>
                    <td className="px-4 py-2 text-right">
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={() => openComment(s)} className="gap-1" data-testid={`ts-comment-${s.id}`}>
                          <MessageSquarePlus className="h-3 w-3" />Commentaire
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => openActivity(s)} className="gap-1" data-testid={`ts-activity-${s.id}`}>
                          <Activity className="h-3 w-3" />Voir activité
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
      {filtered.length === 0 && (
        <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-10 text-center text-slate-500">
          {noScope ? "Aucune classe affectée." : "Aucun élève trouvé."}
        </div>
      )}

      {/* Weekly comment dialog */}
      <Dialog open={!!commentDlg} onOpenChange={(o) => !o && setCommentDlg(null)}>
        <DialogContent className="max-w-lg" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>Commentaire hebdomadaire — {commentDlg?.student?.first_name} {commentDlg?.student?.last_name}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <p className="text-xs text-slate-500">Ce commentaire sera envoyé aux parents dans le rapport hebdomadaire automatique (samedi matin). Vous pouvez le modifier jusqu&apos;à l&apos;envoi.</p>
            <Textarea rows={5} value={comment} onChange={e => setComment(e.target.value)} placeholder="Ex : Fatou a bien progressé en mathématiques cette semaine. Je recommande de réviser les fractions ce week-end." data-testid="wc-text" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCommentDlg(null)}>Annuler</Button>
            <Button onClick={saveComment} disabled={savingComment} className="bg-[#166534] text-white" data-testid="wc-save">
              {savingComment ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Activity detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Activité IA — {detail?.student?.first_name} {detail?.student?.last_name} (30 j)</DialogTitle></DialogHeader>
          {!detail?.usage ? <div className="text-slate-400 py-6 text-center">Chargement...</div> : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-blue-50 rounded-xl p-4 text-center">
                  <div className="text-2xl font-extrabold text-[#1E3A8A]">{detail.usage.total_activities}</div>
                  <div className="text-xs text-slate-500">Activités</div>
                </div>
                <div className="bg-green-50 rounded-xl p-4 text-center">
                  <div className="text-2xl font-extrabold text-[#166534]">{detail.usage.total_time_minutes} min</div>
                  <div className="text-xs text-slate-500">Temps total</div>
                </div>
              </div>
              <div className="space-y-2">
                {(detail.usage.activities || []).map(a => (
                  <div key={a.id} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2">
                    <div>
                      <div className="font-semibold">{ACTIVITY_LABELS[a.activity_type] || a.activity_type}</div>
                      <div className="text-xs text-slate-500">{a.resource_title || a.subject || ""}</div>
                    </div>
                    <div className="text-xs text-slate-400 flex items-center gap-1">
                      <Clock className="h-3 w-3" />{new Date(a.created_at).toLocaleString("fr-FR")}
                    </div>
                  </div>
                ))}
                {(detail.usage.activities || []).length === 0 && (
                  <div className="text-center text-slate-400 py-4">Aucune activité sur les 30 derniers jours.</div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
